// System 8 — procedural DSP.
//
// The brief: "fully procedural: rain on different surfaces, distant traffic, transformer hum, servo
// whine, sign buzz, footsteps that change with surface. Keep the DSP as pure functions so it can be
// rendered to WAV offline and measured."
//
// Everything here is a pure function of (seconds, sampleRate, seed) -> Float32Array. No browser
// APIs, no time, no state: node can render and MEASURE these (harness/audio.mjs), and the browser
// engine plays the identical buffers. The measurement is the gate -- a sound that "should" ring
// longer on metal is asserted to ring longer, numerically, offline.

import { rngFor } from '../rng.js';

const TAU = Math.PI * 2;

function noiseGen(seed) {
  const r = rngFor(`dsp.${seed}`);
  return () => r.f() * 2 - 1;
}

/** One-pole low-pass. fc in Hz. */
function lp(buf, fc, sr) {
  const a = 1 - Math.exp(-TAU * fc / sr);
  let y = 0;
  for (let i = 0; i < buf.length; i++) { y += a * (buf[i] - y); buf[i] = y; }
  return buf;
}

function normalise(buf, target = 0.7) {
  let peak = 1e-9;
  for (let i = 0; i < buf.length; i++) peak = Math.max(peak, Math.abs(buf[i]));
  const g = target / peak;
  for (let i = 0; i < buf.length; i++) buf[i] *= g;
  return buf;
}

/** Make the ends meet so the buffer loops without a click. */
function loopFade(buf, sr, ms = 30) {
  const n = Math.min(buf.length >> 2, Math.round(sr * ms / 1000));
  for (let i = 0; i < n; i++) {
    const t = i / n;
    buf[buf.length - n + i] = buf[buf.length - n + i] * (1 - t) + buf[i] * t;
  }
  return buf;
}

/**
 * Rain on a surface. The surface IS the filter: concrete absorbs (dull broadband), metal both
 * brightens the hiss and RINGS -- each drop excites a damped mode. That ring is why rain on a car
 * roof is recognisable with your eyes shut, and it is what audio.mjs asserts on.
 */
export function rain(surface, seconds = 4, sr = 44100, seed = 1) {
  const n = Math.round(seconds * sr);
  const out = new Float32Array(n);
  const g = noiseGen(`rain.${surface}.${seed}`);
  for (let i = 0; i < n; i++) out[i] = g();
  // The measured claim is "metal is brighter". First render measured them EQUAL (1047 vs 1052 Hz
  // centroid): one pole at 2600 leaves concrete plenty of highs, and the 900-1400 Hz metal rings
  // pulled its centroid DOWN. Concrete now gets two poles at 1400 (dull, absorbent); metal keeps
  // one at 6000 and rings higher. The fence stays where it was -- the sound moved.
  if (surface === 'metal') lp(out, 6000, sr);
  else { lp(out, 1400, sr); lp(out, 1400, sr); }
  // Droplet transients on top of the hiss bed.
  const r = rngFor(`rain.drops.${surface}.${seed}`);
  const drops = Math.round(seconds * (surface === 'metal' ? 26 : 46));
  for (let d = 0; d < drops; d++) {
    const at = Math.floor(r.f() * (n - sr * 0.1));
    const amp = 0.25 + r.f() * 0.65;
    if (surface === 'metal') {
      // Damped mode ~ 900-1400 Hz.
      const f = 1500 + r.f() * 900, decay = 0.03 + r.f() * 0.05;
      for (let i = 0; i < sr * 0.12 && at + i < n; i++) {
        const t = i / sr;
        out[at + i] += amp * Math.sin(TAU * f * t) * Math.exp(-t / decay) * 0.6;
      }
    } else {
      for (let i = 0; i < sr * 0.012 && at + i < n; i++) {
        out[at + i] += amp * (r.f() * 2 - 1) * Math.exp(-i / (sr * 0.004));
      }
    }
  }
  return loopFade(normalise(out, 0.5), sr);
}

/** Distant traffic: brown-ish rumble with slow swells. Nothing above ~150 Hz survives distance. */
export function traffic(seconds = 6, sr = 44100, seed = 1) {
  const n = Math.round(seconds * sr);
  const out = new Float32Array(n);
  const g = noiseGen(`traffic.${seed}`);
  for (let i = 0; i < n; i++) out[i] = g();
  lp(out, 110, sr); lp(out, 110, sr);
  for (let i = 0; i < n; i++) {
    const t = i / sr;
    out[i] *= 0.55 + 0.30 * Math.sin(TAU * 0.09 * t) + 0.15 * Math.sin(TAU * 0.041 * t + 1.7);
  }
  return loopFade(normalise(out, 0.55), sr);
}

/** Transformer hum: mains fundamental plus odd-flavoured harmonics, faintly breathing. */
export function hum(seconds = 4, sr = 44100) {
  const n = Math.round(seconds * sr);
  const out = new Float32Array(n);
  const F = 60;
  const parts = [[F, 1.0], [F * 2, 0.42], [F * 3, 0.28], [F * 5, 0.1]];
  for (let i = 0; i < n; i++) {
    const t = i / sr;
    let v = 0;
    for (const [f, a] of parts) v += a * Math.sin(TAU * f * t);
    out[i] = v * (0.9 + 0.1 * Math.sin(TAU * 0.31 * t));
  }
  return loopFade(normalise(out, 0.4), sr);
}

/** Sign ballast buzz: 120 Hz buzz (rectified mains) with sputter dropouts, like the flicker. */
export function buzz(seconds = 4, sr = 44100, seed = 1) {
  const n = Math.round(seconds * sr);
  const out = new Float32Array(n);
  const r = rngFor(`buzz.${seed}`);
  const gate = new Float32Array(Math.ceil(seconds * 22));
  for (let i = 0; i < gate.length; i++) gate[i] = r.f() < 0.08 ? 0.15 : 1.0;
  for (let i = 0; i < n; i++) {
    const t = i / sr;
    let v = 0;
    for (let h = 1; h <= 9; h += 2) v += Math.sin(TAU * 120 * h * t) / h;
    out[i] = v * gate[Math.floor(t * 22)] * (0.8 + 0.2 * Math.sin(TAU * 0.7 * t));
  }
  return loopFade(normalise(out, 0.35), sr);
}

/** Servo whine: pitch tracks the swing phase -- rises through the stroke, falls to idle. */
export function servo(seconds = 2, sr = 44100) {
  const n = Math.round(seconds * sr);
  const out = new Float32Array(n);
  let ph = 0;
  for (let i = 0; i < n; i++) {
    const t = i / sr;
    const stroke = (t * 1.4) % 1;                      // one leg swing per ~0.7 s
    const f = 700 + 1100 * Math.pow(stroke, 0.8);
    ph += TAU * f / sr;
    out[i] = (Math.sin(ph) * 0.7 + Math.sin(ph * 2.02) * 0.3) * (0.35 + 0.65 * stroke);
  }
  return loopFade(normalise(out, 0.3), sr);
}

/**
 * A footstep on a surface. Same skeleton, different body: an impact burst plus the surface's own
 * resonance. Grate rings (long decay, high mode), tile clicks (bright, short), asphalt/pavement
 * thumps (dull, short). audio.mjs asserts the DECAY ordering and the CENTROID ordering.
 */
export function footstep(surface, sr = 44100, seed = 1) {
  const n = Math.round(sr * 0.5);
  const out = new Float32Array(n);
  const r = rngFor(`step.${surface}.${seed}`);
  const cfg = {
    pavement: { burstFc: 900, thump: 170, decay: 0.055, ring: 0 },
    asphalt: { burstFc: 750, thump: 150, decay: 0.06, ring: 0 },
    tile: { burstFc: 2400, thump: 260, decay: 0.045, ring: 0 },
    grate: { burstFc: 1600, thump: 190, decay: 0.05, ring: 760 },
  }[surface] || { burstFc: 900, thump: 170, decay: 0.055, ring: 0 };

  const burst = new Float32Array(Math.round(sr * 0.02));
  for (let i = 0; i < burst.length; i++) burst[i] = (r.f() * 2 - 1) * Math.exp(-i / (sr * 0.005));
  lp(burst, cfg.burstFc, sr);
  for (let i = 0; i < burst.length; i++) out[i] += burst[i] * 1.2;

  for (let i = 0; i < n; i++) {
    const t = i / sr;
    out[i] += 0.8 * Math.sin(TAU * cfg.thump * t) * Math.exp(-t / cfg.decay);
    if (cfg.ring) out[i] += 0.5 * Math.sin(TAU * cfg.ring * t) * Math.exp(-t / 0.16);
  }
  return normalise(out, 0.65);
}

/** Everything audio.mjs renders and measures, in one map. */
export const RENDERS = {
  rain_concrete: () => rain('concrete'),
  rain_metal: () => rain('metal'),
  traffic: () => traffic(),
  hum: () => hum(),
  buzz: () => buzz(),
  servo: () => servo(),
  step_pavement: () => footstep('pavement'),
  step_tile: () => footstep('tile'),
  step_grate: () => footstep('grate'),
};
